import { useEffect, useState } from "react";
import { Download, X, Share as ShareIcon, Plus } from "lucide-react";

const DISMISS_KEY = "vidmar_install_dismissed_at";
const DISMISS_TTL_MS = 3 * 24 * 60 * 60 * 1000; // 3 days

function isStandalone() {
    if (typeof window === "undefined") return false;
    return (
        window.matchMedia?.("(display-mode: standalone)").matches ||
        window.navigator.standalone === true // iOS Safari
    );
}

function isIOS() {
    if (typeof window === "undefined") return false;
    const ua = window.navigator.userAgent || "";
    return /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
}

function wasRecentlyDismissed() {
    try {
        const ts = Number(localStorage.getItem(DISMISS_KEY));
        return ts && Date.now() - ts < DISMISS_TTL_MS;
    } catch {
        return false;
    }
}

export default function InstallPrompt() {
    const [deferredPrompt, setDeferredPrompt] = useState(null);
    const [visible, setVisible] = useState(false);
    const [ios, setIos] = useState(false);

    useEffect(() => {
        if (isStandalone() || wasRecentlyDismissed()) return;

        if (isIOS()) {
            // iOS: no beforeinstallprompt, we show manual instructions
            const t = setTimeout(() => {
                setIos(true);
                setVisible(true);
            }, 3500);
            return () => clearTimeout(t);
        }

        const handler = (e) => {
            e.preventDefault();
            setDeferredPrompt(e);
            setVisible(true);
        };
        window.addEventListener("beforeinstallprompt", handler);
        return () => window.removeEventListener("beforeinstallprompt", handler);
    }, []);

    const install = async () => {
        if (!deferredPrompt) return;
        deferredPrompt.prompt();
        const { outcome } = await deferredPrompt.userChoice;
        if (outcome === "accepted") {
            setVisible(false);
        }
        setDeferredPrompt(null);
    };

    const dismiss = () => {
        try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch {}
        setVisible(false);
    };

    if (!visible) return null;

    return (
        <div
            className="fixed left-4 right-4 bottom-24 z-[9998] fade-up"
            data-testid="install-prompt"
        >
            <div className="glass rounded-2xl p-4 shadow-2xl flex items-start gap-3 border border-[#FF5722]/30">
                <img src="/icon-192.png" alt="" className="w-14 h-14 rounded-2xl shrink-0" />
                <div className="flex-1 min-w-0">
                    <p className="font-display font-bold text-base">Installer VIDMAR</p>
                    {ios ? (
                        <p className="text-xs text-white/70 font-body mt-1">
                            Appuyez sur <ShareIcon className="w-3 h-3 inline mx-0.5" /> puis <b>"Sur l'écran d'accueil"</b> <Plus className="w-3 h-3 inline mx-0.5" /> pour installer l'app.
                        </p>
                    ) : (
                        <p className="text-xs text-white/70 font-body mt-1">
                            Ajoutez VIDMAR à votre écran d'accueil pour un accès en 1 clic, comme une vraie app.
                        </p>
                    )}
                    <div className="flex gap-2 mt-3">
                        {!ios && (
                            <button
                                data-testid="install-accept"
                                onClick={install}
                                className="orange-btn h-10 px-4 rounded-full font-body font-bold text-sm flex items-center gap-1.5"
                            >
                                <Download className="w-4 h-4" /> Installer
                            </button>
                        )}
                        <button
                            data-testid="install-dismiss"
                            onClick={dismiss}
                            className="h-10 px-4 rounded-full bg-white/10 border border-white/20 font-body font-semibold text-sm"
                        >
                            Plus tard
                        </button>
                    </div>
                </div>
                <button
                    data-testid="install-close"
                    onClick={dismiss}
                    className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center shrink-0"
                >
                    <X className="w-4 h-4" />
                </button>
            </div>
        </div>
    );
}
